import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { BRAND, WA_DEFAULT } from "@/data/site";
import { scrollToTop } from "@/lib/lenis";

const SECTIONS = [
  {
    title: "1. Quem somos",
    body: `Melo Vistos & Passaportes Ltda, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº ${BRAND.cnpj}, com atuação em São Paulo - SP, Brasil, é a controladora dos dados pessoais coletados neste site, nos termos da Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD).`,
  },
  {
    title: "2. Dados que coletamos",
    body: "Coletamos apenas os dados que você fornece voluntariamente nos formulários do site: nome completo, número de WhatsApp/telefone, endereço de e-mail, destino de interesse e o conteúdo da sua mensagem. No agendamento de consultorias, coletamos também a data e o horário escolhidos. Não coletamos dados sensíveis, documentos ou informações de pagamento por este site.",
  },
  {
    title: "3. Finalidade do tratamento",
    body: "Os dados são utilizados exclusivamente para: (a) responder às suas solicitações de contato; (b) realizar a análise preliminar de perfil consular solicitada; (c) confirmar e organizar consultorias agendadas; (d) enviar comunicações sobre o andamento do seu atendimento. Não vendemos, alugamos ou compartilhamos seus dados com terceiros para fins de marketing.",
  },
  {
    title: "4. Base legal",
    body: "O tratamento dos dados pessoais fundamenta-se no consentimento do titular (art. 7º, I, da LGPD), manifestado ao enviar voluntariamente os formulários, e na execução de procedimentos preliminares a contrato (art. 7º, V, da LGPD).",
  },
  {
    title: "5. Armazenamento e segurança",
    body: "Os dados são armazenados em servidores seguros com criptografia em trânsito (SSL/TLS) e acesso restrito à equipe autorizada. Adotamos medidas técnicas e organizacionais adequadas para proteger seus dados contra acessos não autorizados, perda ou alteração.",
  },
  {
    title: "6. Cookies e ferramentas de medição",
    body: "Utilizamos ferramentas de análise e publicidade (como Google Analytics e, quando ativado, Meta Pixel) para medir a audiência e a eficácia das nossas campanhas. Essas ferramentas podem utilizar cookies. Você pode desativar os cookies nas configurações do seu navegador, sem prejuízo ao uso do site.",
  },
  {
    title: "7. Seus direitos (LGPD)",
    body: `Você pode, a qualquer momento, solicitar a confirmação, o acesso, a correção, a anonimização, a portabilidade ou a eliminação dos seus dados pessoais, bem como revogar o consentimento, enviando um e-mail para ${BRAND.email} ou uma mensagem para o WhatsApp ${BRAND.phone}.`,
  },
  {
    title: "8. Retenção",
    body: "Conservamos os dados pelo período necessário ao atendimento das finalidades descritas e ao cumprimento de obrigações legais, após o que são eliminados de forma segura.",
  },
  {
    title: "9. Alterações desta política",
    body: `Esta política pode ser atualizada periodicamente. A versão vigente estará sempre disponível nesta página. Última atualização: outubro de 2026. Dúvidas: ${BRAND.email} • ${BRAND.phone}.`,
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-navy-950">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-gold-400/15 bg-navy-950/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" data-testid="privacy-brand-logo" className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Melo Vistos"
              className="h-10 w-10 rounded-md border border-gold-400/40 object-cover"
            />
            <span className="font-mono-tech text-xs font-semibold tracking-[0.28em] text-slate-100">
              MELO <span className="text-gold-400">VISTOS</span>
            </span>
          </Link>
          <a
            data-testid="privacy-header-whatsapp"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-gold-400/50 bg-gold-400/10 px-5 py-2 text-sm font-medium text-gold-200 transition-colors duration-300 hover:bg-gold-400 hover:text-navy-950"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-32 sm:px-8">
        <Link
          data-testid="privacy-back-link"
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm font-light text-slate-300 transition-colors hover:text-gold-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar ao site
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-3 font-mono-tech text-xs tracking-[0.3em] text-gold-400">
            DOCUMENTO LEGAL
          </p>
          <h1 className="font-serif-display text-3xl font-normal tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Política de <em className="gold-text italic">Privacidade</em>
          </h1>
          <p className="mt-5 text-sm font-light leading-relaxed text-slate-400">
            Em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº
            13.709/2018), descrevemos como coletamos, usamos e protegemos suas
            informações.
          </p>
        </motion.div>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
            >
              <h2 className="font-serif-display text-xl text-gold-200 sm:text-2xl">
                {s.title}
              </h2>
              <p className="mt-3 text-sm font-light leading-relaxed text-slate-300">
                {s.body}
              </p>
            </motion.section>
          ))}
        </div>
      </main>
    </div>
  );
}
